// src/auth/recovery-client.ts
//
// Thin fetch wrapper around the `/recovery` edge function. V3 §5.6.
//
//  - Timeouts: 5 s for `start`, 10 s for `finish`, 5 s for `status`.
//  - Retry on 5xx with exponential backoff (3 attempts). Never retry on 4xx.
//  - Never retry `finish` — it is not idempotent across uid mints.
//
// The caller (RecoveryFlow, anonymous.ts silent path) is responsible for
// rendering outcome-specific copy and for calling `supabase.auth.setSession`
// with any tokens returned.

import { supabase } from './anonymous';
import { getDeviceHint } from './device-hint';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

const TIMEOUT_START_MS  = 5_000;
const TIMEOUT_FINISH_MS = 10_000;
const TIMEOUT_STATUS_MS = 5_000;
const MAX_START_RETRIES = 3;

export type RecoveryOutcome =
  | 'verified'
  | 'expired'
  | 'used'
  | 'exhausted'
  | 'invalid'
  | 'merged'
  | 'verify_other_side';

export interface StartResponse {
  ok: boolean;
  expires_at?: string;
  error?: string;
}

export interface FinishResponse {
  outcome?: RecoveryOutcome;
  winner_player_id?: string | null;
  credits?: number;
  access_token?: string;
  refresh_token?: string;
  attempts_remaining?: number;
  error?: string;
}

export interface StatusResponse {
  attached: boolean;
  verified: boolean;
}

async function fetchWithTimeout(
  url: string, init: RequestInit, timeoutMs: number,
): Promise<Response> {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: ac.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function recoveryUrl(sub = ''): string {
  return `${SUPABASE_URL}/functions/v1/recovery${sub}`;
}

async function postJsonWithRetry(
  url: string,
  body: Record<string, unknown>,
  timeoutMs: number,
  maxRetries: number,
  extraHeaders: Record<string, string> = {},
): Promise<Response> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetchWithTimeout(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...extraHeaders,
          ...(await authHeader()),
        },
        body: JSON.stringify(body),
      }, timeoutMs);
      // Only retry on 5xx. 4xx is a client error — never retry.
      if (res.status >= 500 && attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, 200 * Math.pow(2, attempt)));
        continue;
      }
      return res;
    } catch (e) {
      lastErr = e;
      if (attempt === maxRetries) break;
      await new Promise((r) => setTimeout(r, 200 * Math.pow(2, attempt)));
    }
  }
  throw lastErr ?? new Error('recovery request failed');
}

export interface PowSolution {
  nonce: string;
  solution: string;
  difficulty: number;
}

/**
 * POST /recovery { action: 'start' }.
 */
export async function startRecovery(
  email: string,
  purpose: 'attach' | 'recover',
  captchaToken: string,
  pow: PowSolution,
): Promise<StartResponse> {
  // Send the device hint so the server can populate the NOT NULL
  // recovery_pow_bindings.device_hint_hash on the no-bearer path
  // (issue #254). Best-effort: the server falls back to a
  // `nohint|<ip-prefix>` hash when the header is absent.
  let hintHeaders: Record<string, string> = {};
  try {
    const hint = await getDeviceHint();
    if (hint) hintHeaders = { 'x-rc-device-hint': hint.slice(0, 512) };
  } catch { /* fingerprinting unavailable — server fallback covers it */ }
  const res = await postJsonWithRetry(
    recoveryUrl(),
    { action: 'start', email, purpose, captcha_token: captchaToken, pow },
    TIMEOUT_START_MS,
    MAX_START_RETRIES,
    hintHeaders,
  );
  return await res.json().catch(() => ({ ok: false, error: 'parse_error' }));
}

/**
 * POST /recovery { action: 'finish' } — magic-link or code-entry form.
 * Never retries. Caller decides based on `outcome`.
 */
export async function finishRecovery(
  input: { token: string; powNonce?: string } | { email: string; code: string; powNonce?: string },
): Promise<FinishResponse> {
  const body: Record<string, unknown> = { action: 'finish' };
  if ('token' in input) body.token = input.token;
  else { body.email = input.email; body.code = input.code; }
  if (input.powNonce) body.pow_nonce = input.powNonce;

  const res = await fetchWithTimeout(recoveryUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeader()),
    },
    body: JSON.stringify(body),
  }, TIMEOUT_FINISH_MS);
  return await res.json().catch(() => ({ error: 'parse_error' }));
}

/**
 * POST /recovery { action: 'status' } with a 30 s sessionStorage cache keyed
 * by uid (§4.1).
 */
export async function statusRecovery(forceFresh = false): Promise<StatusResponse> {
  const { data: sess } = await supabase.auth.getSession();
  const uid = sess.session?.user?.id ?? null;
  const cacheKey = uid ? `rc_recovery_status_${uid}` : null;

  if (cacheKey && !forceFresh) {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { at: number; body: StatusResponse };
        if (Date.now() - parsed.at < 30_000) return parsed.body;
      }
    } catch { /* quota / disabled */ }
  }

  const res = await fetchWithTimeout(recoveryUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeader()),
    },
    body: JSON.stringify({ action: 'status' }),
  }, TIMEOUT_STATUS_MS);
  const body = await res.json().catch(() => ({ attached: false, verified: false }));

  if (cacheKey) {
    try { sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), body })); }
    catch { /* quota */ }
  }
  return body as StatusResponse;
}

/**
 * POST /recovery/forget — §4.4. Same-origin POST that clears the
 * HTTP-only `rc_session` cookie so "Start Fresh" actually forgets the
 * prior uid. Without this call, a user who clicked Start Fresh would
 * silently recover back into the same uid on their next visit because
 * the cookie is invisible to client JS.
 *
 * Never throws; swallows network / non-2xx failures. The caller should
 * proceed with the UI transition regardless — worst case is a stale
 * cookie that expires naturally.
 */
export async function forgetCookies(): Promise<{ ok: boolean }> {
  try {
    const res = await fetchWithTimeout(recoveryUrl('/forget'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    }, TIMEOUT_START_MS);
    return { ok: res.ok };
  } catch {
    return { ok: false };
  }
}

/**
 * POST /recovery/silent — HMAC-signed rc_session cookie only (§5.1).
 * Never includes a bearer; returns a minted session on success.
 */
export async function silentRecovery(): Promise<{
  ok: boolean;
  access_token?: string;
  refresh_token?: string;
  reason?: string;
  winner_player_id?: string;
  /** §4.5 — welcome-back age hint (integer days since cookie iat). */
  recovered_age_days?: number;
  /** §4.5 — welcome-back paid-credits hint (drives toast copy variant). */
  has_paid_credits?: boolean;
}> {
  try {
    const res = await fetchWithTimeout(recoveryUrl('/silent'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    }, TIMEOUT_START_MS);
    return await res.json().catch(() => ({ ok: false, reason: 'parse_error' }));
  } catch {
    return { ok: false, reason: 'network_error' };
  }
}
